import { Clone, ContactShadows, Grid, Html, useAnimations } from "@react-three/drei";
import { useDracoGLTF } from "../../../../../utils/useDracoGLTF";
import { useRemapMaterials } from "../../../Materials";
import { useSpring, animated as a, config as springConfig } from "@react-spring/three";

import { createPortal as reactPortal } from "react-dom";

import { useSpring as useWebSpring, animated as aweb } from "@react-spring/web";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { cameraStore } from "../../../../../appStore";
import { ref, useSnapshot } from "valtio";
import { Ebbne, MountWithEbbne, useEbbne, useEbbnenContext } from "@solidwhite/ebbnen";
import { findObjectStartsWithKey } from "../../../../../utils/FindObjects";
import { powerrailProductStore } from "./powerrail-product-store";
import Hotspot from "../../../../ui/Hotspot";
import { useTranslation } from "react-i18next";
import { Color, FrontSide, LoopPingPong, LoopRepeat, MathUtils, MeshBasicMaterial } from "three";
import * as THREE from "three";
import CarrierMovementAnimation from "./CarrierMovementAnimation";
import { FresnelMaterialImpl } from "../../../materials/FresnelMaterial";
import { createPortal, useFrame } from "@react-three/fiber";
import { degToRad } from "three/src/math/MathUtils";
import MeasurementLines from "../../../MeasurementLines.tsx";
import { UIPortal } from "@solidwhite/ui-portal";
import SideBar from "../../../../ui/SideBar/SideBar";
import { LineLabel } from "../../../applications/howitworks/HowitworksProcess1";
import { useRive } from "@rive-app/react-canvas";
import { Rive } from "@rive-app/canvas";
import DescriptionBox from "../../../DescriptionBox";
import { MarkerLabels } from "../../../../ui/MarkerLabel";
import CapacitorAnimation from "./capacitor/CapacitorAnimation";

const Powerrail = ({ ...props }) => {
  const { nodes, scene, animations } = useDracoGLTF("./models/charge/Products_powerrail.glb");

  const { hideBackground } = useSnapshot(powerrailProductStore);

  const { actions } = useAnimations(animations, scene);

  useRemapMaterials(scene);

  const fresnelMaterial = useMemo(() => {
    const mat = new FresnelMaterialImpl();
    mat.transparent = true;
    mat.depthWrite = false;
    mat.opacity = 0.25;
    mat.uniforms.uBaseColor.value = new Color("#000000");
    mat.uniforms.uFresnelColor.value = new Color("#ffffff");
    mat.uniforms.uFresnelPower.value = 3;
    mat.side = FrontSide;

    return mat;
  }, []);

  useEffect(() => {
    nodes.Carrier_1.material = fresnelMaterial;
  }, [fresnelMaterial]);

  const storeCamTargetsObject = (groupRef) => {
    if (groupRef) powerrailProductStore.camTargetsObject = ref(groupRef);
  };

  const addWireframe = (target) => {
    let edges = new THREE.EdgesGeometry(target.geometry.clone(), 30);

    const mat = new THREE.LineBasicMaterial({
      color: new THREE.Color("#ffffff"),
      opacity: 0.025,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      toneMapped: false,
      // blending: THREE.AdditiveBlending,
      stencilWrite: true,
      stencilFunc: THREE.AlwaysStencilFunc,
    });
    let line = new THREE.LineSegments(edges, mat);
    line.computeLineDistances();
    line.renderOrder = 3;
    line.visible = true;

    target.add(line);
  };

  const carrierMaterialTransparent = useMemo(() => {
    const mat = new FresnelMaterialImpl();
    mat.transparent = true;
    mat.depthWrite = true;
    mat.opacity = 0.25;
    mat.uniforms.uBaseColor.value = new Color("#000000");
    mat.uniforms.uFresnelColor.value = new Color("#def1e8");
    mat.uniforms.uFresnelPower.value = 4;
    mat.side = FrontSide;

    return mat;
  }, []);

  useEffect(() => {
    if (nodes.Carrier_transparent.children.length == 0) {
      addWireframe(nodes.Carrier_transparent);
      nodes.Carrier_transparent.material = carrierMaterialTransparent;
    }

    nodes.Spline_electro_0.visible = false;
    nodes.Spline_electro_1.visible = false;
    nodes.Spline_electro_2.visible = false;
  }, []);

  return (
    <>
      <group ref={storeCamTargetsObject} position={[0, 0, 0]}>
        <Clone object={nodes.CameraTargets} />
        {/* <primitive object={nodes.CameraTargets} /> */}
      </group>

      <a.group>
        <primitive object={scene} visible={!hideBackground} />
      </a.group>

      <MountWithEbbne>
        <PowerRailCam camName="CameraTarget_root" />
      </MountWithEbbne>

      <Ebbne id="carrier">
        <Carrier nodes={nodes} actions={actions} />
      </Ebbne>

      <Ebbne id="track">
        <Track nodes={nodes} />
      </Ebbne>
    </>
  );
};

const Carrier = ({ nodes, actions }) => {
  const { selectable, select } = useEbbne();

  return (
    <>
      <PowerrailLabel node={nodes.Label_Carrier} />

      <MountWithEbbne>
        <PowerRailCam camName="CameraTarget_Carrier_0" />
        <UIPortal>
          <SideBar key="Sidebar_carrier" contentId="carrier" />
        </UIPortal>
      </MountWithEbbne>

      <Ebbne id="e-pickup">
        <PowerrailLabel node={nodes.Label_Carrier_Epickup} />
        <EPickup nodes={nodes} />
      </Ebbne>

      <Ebbne id="movement">
        <PowerrailLabel node={nodes.Label_Carrier_Movement} isAnimation />
        <MountWithEbbne>
          <CarrierMovementAnimation nodes={nodes} actions={actions} />
          {/* <PowerRailCam camName="CameraTarget_Carrier_0" /> */}
        </MountWithEbbne>
      </Ebbne>

      <Ebbne id="regulator">
        <PowerrailLabel node={nodes.Label_Carrier_Regulator} />
        <Regulator nodes={nodes} />
      </Ebbne>
    </>
  );
};

const EPickup = ({ nodes }) => {
  const { active } = useEbbne();
  useEffect(() => {
    powerrailProductStore.hideBackground = active;
  }, [active]);

  return (
    <group rotation={[0, degToRad(-90), 0]}>
      <group position={nodes.Carrier_root.position} rotation={nodes.Carrier_root.rotation}>
        <Clone object={nodes.Pickup_root} visible={active}></Clone>
        <MountWithEbbne>
          <PowerRailCam camName="CameraTarget_EPickup" />
          <MeasurementLines node={nodes.Measurements_pickup} />
          <UIPortal>
            <SideBar key="Sidebar_pickup" contentId="e-pickup" />
          </UIPortal>
          <GreenGrid position={[0, -0.15, 0]} />
        </MountWithEbbne>
      </group>
    </group>
  );
};

const Regulator = ({ nodes }) => {
  const { active } = useEbbne();
  useEffect(() => {
    powerrailProductStore.hideBackground = active;
  }, [active]);

  return (
    <group rotation={[0, degToRad(-90), 0]}>
      <group position={nodes.Carrier_root.position} rotation={nodes.Carrier_root.rotation}>
        <Clone object={nodes.Regulator_root} visible={active}></Clone>
        <MountWithEbbne>
          <PowerRailCam camName="CameraTarget_Regulator" />
          <MeasurementLines node={nodes.Measurements_regulator} />
          <UIPortal>
            <SideBar key="Sidebar_regulator" contentId="regulator" />
          </UIPortal>
          <GreenGrid position={[0, -0.25, 0.35]} />
        </MountWithEbbne>

        <group visible={active}></group>
      </group>
    </group>
  );
};

const Track = ({ nodes }) => {
  const { locationHook } = useEbbnenContext();
  const [_, setLocation] = locationHook();
  const { t } = useTranslation();

  return (
    <>
      <PowerrailLabel node={nodes.Label_Track} />

      <MountWithEbbne>
        <Hotspot
          position={nodes.Label_Carrier_Movement.position}
          text={t("L4_MOVEMENT_NAME")}
          topLeft
          onClick={(e) => {
            e.stopPropagation();
            setLocation("/charge/c-products/p-powerrail/carrier/movement");
          }}
          delay={0}
          isAnimation
          // id={hotSpots[1].name}
          // key={hotSpots[1].name}
        />
      </MountWithEbbne>

      <MountWithEbbne>
        <PowerRailCam camName="CameraTarget_Track_0" />
        <UIPortal>
          <SideBar key="Sidebar_track" contentId="track" />
        </UIPortal>
      </MountWithEbbne>

      <MountWithEbbne mountedWhen="active-and-below">
        <CarrierHider nodes={nodes} />
      </MountWithEbbne>

      <Ebbne id="track-supply">
        <PowerrailLabel node={nodes.Label_Track_Supply} />

        <MountWithEbbne>
          <PowerRailCam camName="CameraTarget_Track_Supply" />
          <UIPortal>
            <SideBar key="Sidebar_Track_Supply" contentId="track-supply" />
          </UIPortal>
        </MountWithEbbne>
      </Ebbne>
      <Ebbne id="cable">
        <PowerrailLabel node={nodes.Label_Track_Cable} />

        <Cable />

        <MountWithEbbne>
          <PowerRailCam camName="CameraTarget_Cable" />
          <UIPortal>
            <SideBar key="Sidebar_cable" contentId="cable" />
          </UIPortal>
        </MountWithEbbne>
      </Ebbne>
      <Ebbne id="capacitor">
        <PowerrailLabel node={nodes.Label_Track_Capacitor} />

        <Capacitor powerrailNodes={nodes} />

        <MountWithEbbne>
          <PowerRailCam camName="CameraTarget_Capacitor" />
          <UIPortal>
            <SideBar key="Sidebar_capacitor" contentId="capacitor" />
          </UIPortal>
        </MountWithEbbne>
      </Ebbne>
      <Ebbne id="primary-track">
        <PowerrailLabel node={nodes.Label_Track_Primary} />

        <PrimaryTrack />

        <MountWithEbbne>
          <PowerRailCam camName="CameraTarget_Primary_Track" />
          <UIPortal>
            <SideBar key="Sidebar_primary-track" contentId="primary-track" />
          </UIPortal>
        </MountWithEbbne>
      </Ebbne>
    </>
  );
};

const Cable = () => {
  const { nodes, scene, animations } = useDracoGLTF("./models/charge/Powerrail_cable.glb");

  const { t } = useTranslation();

  const { active } = useEbbne();
  useEffect(() => {
    powerrailProductStore.hideBackground = active;
  }, [active]);

  return (
    <>
      <group>
        <MountWithEbbne>
          <primitive object={scene} />
          <group position={[2.992, 1.75, 0.177]}>
            <GreenGrid />
          </group>
          <MarkerLabels node={nodes.markers_cable} />
          {/* <LineLabel
            scale={0.15}
            text={t("L6_CHARGE_PRODUCTS_POWERRAIL_CABLE_COPPER")}
            position={nodes.Label_Cable_1.position}
            rotation={[0, MathUtils.degToRad(50), 0]}
          />
          <LineLabel
            position={nodes.Label_Cable_2.position}
            rotation={[0, MathUtils.degToRad(20), 0]}
            scale={0.15}
            text={t("L6_CHARGE_PRODUCTS_POWERRAIL_CABLE_MATERIAL")}
          /> */}
          <MeasureLabel position={nodes.Label_Cable_Width.position} rotation={nodes.Label_Cable_Width.rotation} />
        </MountWithEbbne>
      </group>
    </>
  );
};

const PrimaryTrack = () => {
  const { nodes, scene, animations } = useDracoGLTF("./models/charge/Powerrail_primary_track.glb");

  const { t } = useTranslation();

  // useRemapMaterials(scene);

  const { active } = useEbbne();
  useEffect(() => {
    powerrailProductStore.hideBackground = active;
  }, [active]);

  const addWireframe = (target, color = new THREE.Color("#ff0000")) => {
    if (target.userData.hasLine) {
      // console.log("schon eine drauf");
      return;
    }

    let edges = new THREE.EdgesGeometry(target.geometry.clone(), 60);

    const mat = new THREE.LineBasicMaterial({
      color: color,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
      stencilWrite: true,
      stencilFunc: THREE.AlwaysStencilFunc,
    });
    let line = new THREE.LineSegments(edges, mat);
    line.computeLineDistances();
    line.renderOrder = 3;
    line.visible = true;

    target.parent.add(line);
    target.visible = false;
    target.userData.hasLine = true;
  };

  useLayoutEffect(() => {
    addWireframe(nodes.ME_Trackhalter_sep, new THREE.Color("#61855d"));
    addWireframe(nodes.ME_Kabelhalter_sep, new THREE.Color("#0e8603"));
    let cablemat = new FresnelMaterialImpl();
    cablemat.transparent = true;
    cablemat.depthWrite = false;
    cablemat.depthTest = false;
    cablemat.uniforms.uBaseColor.value = new THREE.Color("#222222");
    cablemat.uniforms.uFresnelColor.value = new THREE.Color("#b9ddb6");
    cablemat.uniforms.uFresnelPower.value = 3;
    cablemat.blending = THREE.AdditiveBlending;
    nodes.ME_cable_sep.material = cablemat;

    // addWireframe(nodes.ME_cable_sep, new THREE.Color("#ffffff"));
  }, []);

  return (
    <>
      <group>
        <MountWithEbbne>
          <primitive object={scene} />
          <group position={[1.8, 1.15, 2.2]}>
            <GreenGrid />
          </group>
          <MarkerLabels node={nodes.markers_primary_track} />
          {/* <LineLabel
            scale={0.05}
            text={t("L6_CHARGE_PRODUCTS_POWERRAIL_PRIMARYTRACK_BRACKETS")}
            position={nodes.Label_Track_1.position}
            rotation={[0, MathUtils.degToRad(0), 0]}
          />
          <LineLabel
            scale={0.05}
            text={t("L6_CHARGE_PRODUCTS_POWERRAIL_PRIMARYTRACK_CLAMP")}
            position={nodes.Label_Track_2.position}
            rotation={[0, MathUtils.degToRad(60), 0]}
            right
          />
          <LineLabel
            scale={0.05}
            text={t("L6_CHARGE_PRODUCTS_POWERRAIL_PRIMARYTRACK_CABLE")}
            position={nodes.Label_Track_3.position}
            rotation={[0, MathUtils.degToRad(0), 0]}
            bottom
          /> */}
        </MountWithEbbne>
      </group>
    </>
  );
};

const Capacitor = ({ powerrailNodes }) => {
  const { active } = useEbbne();

  const { nodes, scene, animations } = useDracoGLTF("./models/charge/Powerrail_capacitor.glb");

  const { t } = useTranslation();

  useEffect(() => {
    if (powerrailNodes.ME_Aluprofil_capacitor) {
      let matClone = powerrailNodes.ME_Aluprofil_capacitor.material.clone();
      matClone.transparent = true;
      matClone.depthWrite = false;
      powerrailNodes.ME_Aluprofil_capacitor.material = matClone;
    }
  }, []);

  const hideAluSpring = useSpring({
    opacity: active ? 0.1 : 1,
    onChange: ({ value }) => {
      if (powerrailNodes.ME_Aluprofil_capacitor) {
        powerrailNodes.ME_Aluprofil_capacitor.material.opacity = value.opacity;
      }
    },
  });

  return (
    <>
      <MountWithEbbne>
        {/* <LineLabel
          position={nodes.Label_Capacitor.position}
          rotation={[0, MathUtils.degToRad(0), 0]}
          scale={0.2}
          text={t("L6_CHARGE_PRODUCTS_POWERRAIL_CAPACITOR_BOX")}
        /> */}
        <MarkerLabels node={nodes.markers_capacitor} />
        <UIPortal>
          <DescriptionBox key="CapacitorDescriptionBox">L6_CHARGE_PRODUCTS_POWERRAIL_CAPACITOR_DESCRIPTION</DescriptionBox>
        </UIPortal>

        <CapacitorAnimation nodes={nodes} />
      </MountWithEbbne>
    </>
  );
};

function drawLine(ctx, time, canvasSize) {
  const x = ((time * canvasSize) / 10) % canvasSize;
  ctx.beginPath();
  ctx.lineWidth = 3;
  ctx.strokeStyle = "white";
  ctx.moveTo(x, 0);
  ctx.lineTo(x, canvasSize);
  ctx.stroke();
}

const MeasureLabel = (props) => {
  const [c, cTexture] = useMemo(() => {
    const c = new OffscreenCanvas(500, 500);

    const cTexture = new THREE.CanvasTexture(c);
    cTexture.generateMipmaps = true;
    return [c, cTexture];
  }, []);

  const riveInstance = useMemo(() => {
    const r = new Rive({
      canvas: c,
      src: "./assets/rive/enrx_measure.riv",
      autoplay: true,
    });
    return r;
  }, []);

  useFrame(() => {
    cTexture.needsUpdate = true;
  });

  return (
    <group {...props} scale={0.75}>
      <mesh>
        <planeGeometry args={[0.25, 0.25]} />
        <meshBasicMaterial map={cTexture} transparent depthWrite={false} depthTest={false}></meshBasicMaterial>
      </mesh>
      <mesh rotation={[0, THREE.MathUtils.degToRad(180), 0]}>
        <planeGeometry args={[0.25, 0.25]} />
        <meshBasicMaterial map={cTexture} transparent depthWrite={false} depthTest={false}></meshBasicMaterial>
      </mesh>
    </group>
  );
};

const PowerrailLabel = ({ isAnimation = false, node }) => {
  const { selectable, select } = useEbbne();
  const { t } = useTranslation();

  return (
    <>
      {selectable && (
        <Hotspot
          position={node.position}
          onClick={select}
          text={node.userData.textId ? t(node.userData.textId) : "LABEL"}
          isAnimation={isAnimation}
        />
      )}
    </>
  );
};

const PowerRailCam = ({ camName = "CameraTarget_root" }) => {
  useEffect(() => {
    if (powerrailProductStore.camTargetsObject) {
      let cam = findObjectStartsWithKey(camName, powerrailProductStore.camTargetsObject);
      cameraStore.cameraTargetSettings = ref(cam);
    }
  }, []);
  return null;
};

const CarrierHider = ({ nodes }) => {
  useEffect(() => {
    nodes.Carrier_root.visible = false;
    return () => {
      nodes.Carrier_root.visible = true;
    };
  }, []);
  return null;
};

const GreenGrid = ({ position }) => {
  const gridRef = useRef();

  useLayoutEffect(() => {
    gridRef.current.material.depthWrite = false;
    gridRef.current.material.transparent = true;
    gridRef.current.material.blending = THREE.AdditiveBlending;
  }, []);
  return (
    <Grid
      ref={gridRef}
      position={position}
      cellSize={0.025}
      sectionSize={0.125}
      cellColor="#004100"
      cellThickness={1}
      sectionColor="#015501"
      sectionThickness={1.5}
      fadeDistance={3}
      fadeStrength={7}
      infiniteGrid
      depthWrite={false}
    />
  );
};

<gridMaterial
  cellSize={0.025}
  sectionSize={0.125}
  cellColor="#004100"
  cellThickness={1}
  sectionColor="#015501"
  sectionThickness={1.5}
/>;

export default Powerrail;
